import React, { useCallback, useRef, useState } from 'react';

import { Button } from '@copilot/ui';
import { PRODUCT_NAME } from '../constants/brand';
import { ANALYTICS_EVENTS, track } from '../services/analytics';
import { reportError } from '../services/errorTracking';
import * as billingApi from '../services/billingApi';
import * as pricingApi from '../services/pricingApi';

const cycleLabel = (cycle) => (cycle === 'yearly' ? 'Yearly' : 'Monthly');

// Web's paid-plan checkout: Razorpay's checkout window runs in the page,
// then the backend's webhook - never this code - grants the plan. Shared
// by the in-app plan picker and the public pricing page.
//
// start(plan, cycle) resolves once the flow is over: activated, still
// activating ("delayed" - payment received, webhook not landed yet), or
// dismissed without paying. It rejects when checkout could not open, or
// when the payment could not be confirmed (the webhook still reconciles
// it - the message says so).
export function useRazorpayCheckout({ user, onActivated } = {}) {
  // { plan, cycle, stage: 'opening'|'awaiting_payment'|'verifying'|
  //   'activating'|'activated'|'delayed', message }
  const [stage, setStage] = useState(null);
  const onActivatedRef = useRef(onActivated);
  onActivatedRef.current = onActivated;

  // Polls until the webhook has granted the plan. Matches plan id AND
  // cycle AND payment_state: a bare "the plan changed" check never fires
  // for a same-plan monthly<->yearly switch or a past_due recovery.
  const waitForActivation = useCallback(async (plan, cycle, attempts = billingApi.ACTIVATION_POLL_ATTEMPTS) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, billingApi.ACTIVATION_POLL_INTERVAL_MS);
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        const summary = await pricingApi.getMySubscription();
        const sub = summary.subscription;
        if (sub?.plan?.id === plan.id && sub?.billing_cycle === cycle && sub?.payment_state === 'paid') {
          return true;
        }
      } catch (_) {
        // A failed poll is not a failed payment - keep trying.
      }
    }
    return false;
  }, []);

  const finish = useCallback(
    async (plan, cycle, attempts) => {
      const activated = await waitForActivation(plan, cycle, attempts);
      if (activated) {
        setStage({ plan, cycle, stage: 'activated', message: `You're now on ${plan.name} (${cycleLabel(cycle)}).` });
        track(ANALYTICS_EVENTS.PURCHASE_COMPLETED, { kind: 'subscription', plan_id: plan.id, billing_cycle: cycle });
        onActivatedRef.current?.(plan);
        return 'activated';
      }
      // Not a failure: the payment succeeded, the webhook just has not
      // landed yet. Never say "failed" here.
      setStage({
        plan,
        cycle,
        stage: 'delayed',
        message:
          'Payment successful. Activation is still being processed - refresh your billing status shortly or ' +
          'contact support if it does not update.',
      });
      return 'delayed';
    },
    [waitForActivation]
  );

  const start = useCallback(
    async (plan, cycle) => {
      setStage({ plan, cycle, stage: 'opening', message: 'Opening checkout…' });

      let checkout;
      let Razorpay;
      try {
        [checkout, Razorpay] = await Promise.all([
          billingApi.startCheckout(plan.id, cycle),
          billingApi.loadCheckoutScript(),
        ]);
      } catch (err) {
        setStage(null);
        track(ANALYTICS_EVENTS.PURCHASE_FAILED, {
          kind: 'subscription',
          plan_id: plan.id,
          reason: err?.code || 'checkout_unavailable',
        });
        // Category and code only - never payment details (services/monitoring.js).
        reportError('billing_checkout', err);
        throw err;
      }

      track(ANALYTICS_EVENTS.CHECKOUT_STARTED, { kind: 'subscription', plan_id: plan.id, billing_cycle: cycle });
      setStage({ plan, cycle, stage: 'awaiting_payment', message: 'Complete the payment in the Razorpay window.' });

      return new Promise((resolve, reject) => {
        // The provider takes the amount from the subscription it already
        // holds - nothing passed here can change what is charged.
        const instance = new Razorpay({
          key: checkout.key_id,
          subscription_id: checkout.provider_subscription_id,
          name: PRODUCT_NAME,
          description: `${checkout.plan_name} (${checkout.billing_cycle})`,
          prefill: { email: user?.email, name: user?.full_name },
          handler: async (response) => {
            setStage({ plan, cycle, stage: 'verifying', message: 'Payment successful. Verifying…' });
            try {
              await billingApi.verifyCheckout(response);
              setStage({ plan, cycle, stage: 'activating', message: 'Payment successful. Activating your plan…' });
              resolve(await finish(plan, cycle));
            } catch (err) {
              // The payment may well have succeeded - only the
              // confirmation failed. Say so; the webhook reconciles it.
              setStage(null);
              track(ANALYTICS_EVENTS.PURCHASE_FAILED, {
                kind: 'subscription',
                plan_id: plan.id,
                reason: 'confirmation_failed',
              });
              // Category and code only - never payment details (services/monitoring.js).
              reportError('billing_confirmation', err);
              reject(
                new Error(
                  err?.message ||
                    'We could not confirm your payment. If money was taken it will be reconciled automatically.'
                )
              );
            }
          },
          modal: {
            // Closed without paying - not an error.
            ondismiss: () => {
              setStage(null);
              resolve('dismissed');
            },
          },
        });
        instance.open();
      });
    },
    [finish, user]
  );

  // A manual re-check after activation was reported as still pending.
  const recheck = useCallback(async () => {
    if (!stage) return;
    const { plan, cycle } = stage;
    setStage({ plan, cycle, stage: 'activating', message: 'Checking again…' });
    const outcome = await finish(plan, cycle, 3);
    if (outcome === 'delayed') {
      setStage({
        plan,
        cycle,
        stage: 'delayed',
        message:
          'Still processing. Payment was received, but activation has not come through yet - refresh your ' +
          'billing status shortly or contact support if it does not update.',
      });
    }
  }, [finish, stage]);

  return { stage, start, recheck };
}

// The checkout's progress, under the plan picker's controls.
export function CheckoutStageNotice({ stage, onRecheck, onGoToBilling }) {
  if (!stage) return null;
  return (
    <div className={`form-alert billing-alert-notice checkout-stage-${stage.stage}`} role="status">
      {stage.message}
      {stage.stage === 'delayed' && (
        <div className="checkout-stage-actions">
          <Button variant="secondary" onClick={onRecheck}>
            Check again
          </Button>
          <Button onClick={onGoToBilling}>Go to Billing</Button>
        </div>
      )}
    </div>
  );
}
